// Client layer (TS-2.5.04), pure except for the sessionStorage read/write it exists to wrap. This
// is a per-viewer session convenience, not a portfolio rule - the boundary
// (src/Evergreen.Clients.PortalApi/Boundary/PageDefaults.cs) owns the actual default (DefaultSize =
// 50) and ceiling (MaximumSize = 500); this file only remembers which of the four choices this
// browser tab last picked for the Projects list, for the current session. The house has no
// preference-persistence pattern yet (D-259), so every call is wrapped in try/catch: sessionStorage
// can throw (private browsing, quota, storage disabled by policy) and losing the remembered choice
// must never crash the page - it just falls back to the in-memory default for that load.
const STORAGE_KEY = "eg.projects.pageSize";

export const PAGE_SIZE_OPTIONS = [10, 25, 50] as const;

// D-259: mirrors PageDefaults.MaximumSize verbatim (backend, read-only to this feature) - the
// largest page this boundary will honor whatever a caller asks for. "All" requests that ceiling
// rather than an unbounded page, which the API would refuse (the same class of guard IM-4's junk
// sweep exercises for absurd sizes). If the portfolio ever exceeds 500 projects, "All" still only
// returns the first 500 and the pager remains visible - the honest behavior, not a lie that
// everything fit.
export const ALL_PAGE_SIZE = 500;

export type PageSizeChoice = (typeof PAGE_SIZE_OPTIONS)[number] | typeof ALL_PAGE_SIZE;

const VALID_CHOICES: readonly number[] = [...PAGE_SIZE_OPTIONS, ALL_PAGE_SIZE];

function isPageSizeChoice(value: number): value is PageSizeChoice {
  return VALID_CHOICES.includes(value);
}

/** Returns the remembered choice for this session, or `null` if none was stored (or storage is unavailable). */
export function readPageSizePreference(): PageSizeChoice | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      return null;
    }
    const parsed = Number(raw);
    return isPageSizeChoice(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Best-effort only: a failure to persist leaves the page working with its in-memory choice. */
export function writePageSizePreference(value: PageSizeChoice): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Ignored: see file header.
  }
}
